import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import PageShell from "../../components/dashboard/PageShell";
import RolesPermissionsPanel from "../../components/dashboard/RolesPermissionsPanel";
import { USE_MOCK_AUTH } from "../../config/api";
import { ROLES } from "../../config/roles";
import { OPS_PERMISSION_GROUPS, OPS_PERMISSIONS as P } from "../../config/permissions";
import usePermissions from "../../hooks/usePermissions";
import { useLiveEventTick } from "../../hooks/useLiveRefresh";
import { clearOpsPermissionsCache, fetchOpsPermissions } from "../../services/permissionsApi";
import {
  createOpsRole,
  clearOpsRolesCache,
  deleteOpsRole,
  fetchOpsRoles,
  updateOpsRole,
} from "../../services/rolesApi";
import { fetchTeamMembers } from "../../services/teamMembersApi";
import { showError } from "../../utils/toast";
import {
  clearRolesForApiLoad,
  createCustomRole,
  deleteCustomRole,
  getRoleApiSlug,
  getRoleDisplayLabel,
  getRoleMeta,
  getRolePermissions,
  hydrateRolesFromApi,
  isRoleLocked,
  isSystemRole,
  listRoles,
  removeRoleFromStore,
  resetRolePermissions,
  resetRolesToLocalDefaults,
  setRoleMeta,
  setRolePermissions,
  subscribeRolePermissions,
  upsertRoleFromApi,
} from "../../data/opsRolePermissionsStore";
import {
  listOpsUsers,
  syncOpsUsersPermissionsForRole,
} from "../../data/opsUsersStore";

function permissionKeysToSlugs(keys, byKey) {
  const missing = [];
  const slugs = [];
  for (const key of keys) {
    const slug = byKey?.[key]?.permission_slug;
    if (slug) slugs.push(slug);
    else missing.push(key);
  }
  return { slugs, missing };
}

function countUsersForRoleName(users, role) {
  const roleUuid = getRoleApiSlug(role);
  return users.filter(
    (u) => u.role === role || u.role_slug === role || (roleUuid && u.role_slug === roleUuid)
  ).length;
}

export default function RolesPage() {
  const { hasPermission, isSuperAdmin } = usePermissions();
  const token = useSelector((state) => state.auth.token);
  const [portalUsers, setPortalUsers] = useState([]);
  const [roles, setRoles] = useState(listRoles);
  const [permissionGroups, setPermissionGroups] = useState(OPS_PERMISSION_GROUPS);
  const [permissionByKey, setPermissionByKey] = useState({});
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [rolesError, setRolesError] = useState(null);
  const useLiveApi = Boolean(token) && !USE_MOCK_AUTH;

  const canManage = hasPermission(P.OPS_USERS_MANAGE) || isSuperAdmin;
  const liveRoleTick = useLiveEventTick(/^(role\.|team\.)/);

  useEffect(() => {
    return subscribeRolePermissions(() => {
      setRoles(listRoles());
      if (!useLiveApi) {
        setPortalUsers(listOpsUsers());
      }
    });
  }, [useLiveApi]);

  const refreshRolesFromApi = useCallback(async () => {
    if (!useLiveApi) return listRoles();
    clearOpsRolesCache();
    const apiRoles = await fetchOpsRoles(token);
    hydrateRolesFromApi(apiRoles);
    setRoles(listRoles());
    return listRoles();
  }, [token, useLiveApi]);

  useEffect(() => {
    let cancelled = false;

    async function loadCatalog() {
      setCatalogLoading(true);
      setRolesError(null);
      try {
        if (useLiveApi) {
          clearRolesForApiLoad();
          setRoles([]);
        } else {
          resetRolesToLocalDefaults();
          setRoles(listRoles());
        }

        const [catalog, apiRoles, members] = await Promise.all([
          fetchOpsPermissions(token),
          useLiveApi ? fetchOpsRoles(token) : Promise.resolve(null),
          useLiveApi ? fetchTeamMembers(token).catch(() => []) : Promise.resolve(listOpsUsers()),
        ]);
        if (cancelled) return;

        if (catalog.groups?.length) {
          setPermissionGroups(catalog.groups);
        }
        setPermissionByKey(catalog.byKey || {});
        setPortalUsers(members || []);

        if (useLiveApi) {
          hydrateRolesFromApi(apiRoles || []);
          setRoles(listRoles());
          if (!apiRoles?.length) {
            setRolesError("No roles returned from the server.");
          }
        }
      } catch (err) {
        if (!cancelled) {
          setRolesError(err.message || "Could not load roles and permissions.");
          showError(err.message || "Could not load roles and permissions.");
        }
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    }

    loadCatalog();
    return () => {
      cancelled = true;
    };
  }, [token, useLiveApi]);

  useEffect(() => {
    if (!liveRoleTick || !useLiveApi) return undefined;
    clearOpsPermissionsCache();
    refreshRolesFromApi().catch((err) => {
      showError(err.message || "Could not refresh roles.");
    });
    fetchTeamMembers(token)
      .then((members) => setPortalUsers(members || []))
      .catch(() => {});
    return undefined;
  }, [liveRoleTick, refreshRolesFromApi, token, useLiveApi]);

  const handleSetRolePermissions = (role, permissions) => {
    const next = setRolePermissions(role, permissions);
    if (!useLiveApi) {
      syncOpsUsersPermissionsForRole(role, next);
      setPortalUsers(listOpsUsers());
    }
    return next;
  };

  const handleResetRolePermissions = (role) => {
    const next = resetRolePermissions(role);
    if (!useLiveApi) {
      syncOpsUsersPermissionsForRole(role, next);
      setPortalUsers(listOpsUsers());
    }
    return next;
  };

  const handleCreateRole = async ({ label, permissions }) => {
    if (!permissions?.length) {
      throw new Error("Select at least one permission to create a role.");
    }

    if (!useLiveApi) {
      const slug = createCustomRole({ label, permissions });
      setRoles(listRoles());
      return slug;
    }

    const { slugs, missing } = permissionKeysToSlugs(permissions, permissionByKey);
    if (missing.length) {
      throw new Error("Some selected permissions are missing slugs. Reload the catalog and try again.");
    }
    if (!slugs.length) {
      throw new Error("Select at least one permission to create a role.");
    }

    await createOpsRole(token, { name: label, permissions: slugs });
    await refreshRolesFromApi();
    return label;
  };

  const handleSaveRole = async ({ role, label, permissions }) => {
    if (!permissions?.length) {
      throw new Error("A role must keep at least one permission.");
    }

    if (!useLiveApi) {
      if (label) setRoleMeta(role, { label });
      handleSetRolePermissions(role, permissions);
      return;
    }

    const { slugs, missing } = permissionKeysToSlugs(permissions, permissionByKey);
    if (missing.length) {
      throw new Error("Some selected permissions are missing slugs. Reload the catalog and try again.");
    }

    const meta = getRoleMeta(role);
    const apiName = meta.system ? role : label.trim() || role;
    const roleSlug = getRoleApiSlug(role);
    const updated = await updateOpsRole(token, roleSlug, {
      name: apiName,
      permissions: slugs,
    });
    if (!updated.permissions?.length) {
      updated.permissions = permissions;
    }
    const key = upsertRoleFromApi(updated, { previousName: role });
    if (meta.system && label?.trim()) {
      setRoleMeta(key, { label: label.trim() });
    }
    setRoles(listRoles());
    await refreshRolesFromApi();
  };

  const handleDeleteRole = async (role) => {
    if (isRoleLocked(role)) return false;
    const assigned = countUsersForRoleName(portalUsers, role);
    if (assigned > 0) return false;

    if (!useLiveApi) {
      return deleteCustomRole(role);
    }

    const roleSlug = getRoleApiSlug(role);
    await deleteOpsRole(token, roleSlug);
    removeRoleFromStore(role);
    await refreshRolesFromApi();
    return true;
  };

  return (
    <PageShell
      title="Roles & permissions"
      description="Define what each ops role can see and do across the portal."
    >
      {catalogLoading ? (
        <div className="mb-3 rounded-xl border border-primary/[0.07] bg-primary/[0.02] px-4 py-3 text-xs text-primary/50">
          Loading roles and permissions from the server…
        </div>
      ) : null}
      {rolesError && !catalogLoading ? (
        <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-800">
          {rolesError}
        </div>
      ) : null}
      <RolesPermissionsPanel
        roles={roles}
        getRoleLabel={getRoleDisplayLabel}
        setRoleMeta={setRoleMeta}
        permissionGroups={permissionGroups}
        getRolePermissions={getRolePermissions}
        setRolePermissions={handleSetRolePermissions}
        resetRolePermissions={handleResetRolePermissions}
        createRole={handleCreateRole}
        saveRole={handleSaveRole}
        deleteRole={handleDeleteRole}
        isRoleLocked={isRoleLocked}
        isSystemRole={isSystemRole}
        subscribe={subscribeRolePermissions}
        lockedRoles={[ROLES.SUPER_ADMIN]}
        lockedNotice="Super Admin always has full access. Its name and permissions cannot be changed."
        countUsersForRole={(role) => countUsersForRoleName(portalUsers, role)}
        canEdit={canManage}
      />
    </PageShell>
  );
}
